import { createAsyncThunk } from "@reduxjs/toolkit";

import { type ProductClientNote } from "@fitness-app/data-models/entities/ProductClient";

import { type ActionParams, type AsyncThunkCreator } from "../../../index";
import { PRODUCT_CLIENT_REDUCER } from "../types";

type Payload = {
  productClientId: string;
  noteId: string;
};

/** Removes a note from the client's timeline (TRE-1511). */
export const deleteProductClientNote = createAsyncThunk<
  { notes: ProductClientNote[] },
  ActionParams<Payload>,
  AsyncThunkCreator<string>
>(
  `${PRODUCT_CLIENT_REDUCER}/deleteProductClientNote`,
  async ({ payload, onSuccess, onFailure }, { rejectWithValue, extra: { db } }) => {
    const { data, error: fetchError } = await db
      .from("product_client")
      .select("notes")
      .eq("id", payload.productClientId)
      .single();

    if (fetchError) {
      onFailure?.();

      return rejectWithValue(fetchError.message);
    }

    const current = (data?.notes as ProductClientNote[] | null) ?? [];
    const notes = current.filter((note) => note.id !== payload.noteId);

    // `select()` is what makes an RLS refusal visible: without it PostgREST
    // answers 204 for a row the policy filtered out, so a blocked write looks
    // exactly like a successful one (TRE-2143).
    const { data: updated, error } = await db
      .from("product_client")
      .update({ notes })
      .eq("id", payload.productClientId)
      .select("id");

    if (error) {
      onFailure?.();

      return rejectWithValue(error.message);
    }

    if (!updated.length) {
      onFailure?.();

      return rejectWithValue("No product client row was updated");
    }

    onSuccess?.();

    return { notes };
  },
);
